import { useEffect, useState } from 'react';
import { editorActivityApi } from '../../../api/editorActivityApi';
import type { EditorActivityDate, EditorSession } from '../../../types/editorActivity';

interface EditorActivityDetailProps {
  userId: number;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(ymd: string): string {
  // ymd is YYYY-MM-DD; render without re-applying a timezone shift.
  const parts = ymd.split('-').map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hrs > 0) return `${hrs}h ${remMins}m`;
  return `${remMins}m`;
}

function SessionRow({ session }: { session: EditorSession }) {
  return (
    <tr className="border-t border-line">
      <td className="py-1 pr-4 text-ink">{formatTime(session.loginAt)}</td>
      <td className="py-1 pr-4 text-ink">
        {session.inProgress ? (
          <span className="italic text-amber-700">in progress</span>
        ) : (
          formatTime(session.endedAt as string)
        )}
      </td>
      <td className="py-1 pr-4 text-ink">
        {session.inProgress ? (
          <span className="text-ink-soft">—</span>
        ) : (
          formatDuration(session.durationSeconds as number)
        )}
      </td>
      <td className="py-1 text-ink-soft">
        {session.endReason === 'timeout' ? 'timed out' : session.endReason === 'logout' ? 'logged out' : ''}
      </td>
    </tr>
  );
}

function DateBlock({ date }: { date: EditorActivityDate }) {
  return (
    <div className="mb-4">
      <h4 className="font-semibold text-ink mb-1">{formatDate(date.date)}</h4>
      <table className="w-full text-sm mb-2">
        <thead>
          <tr className="text-left text-ink-soft">
            <th className="font-medium pr-4 pb-1">Login</th>
            <th className="font-medium pr-4 pb-1">Logout</th>
            <th className="font-medium pr-4 pb-1">Time spent</th>
            <th className="font-medium pb-1">End</th>
          </tr>
        </thead>
        <tbody>
          {date.sessions.map((s, i) => (
            <SessionRow key={i} session={s} />
          ))}
        </tbody>
      </table>
      <div className="text-sm text-ink">
        <span className="font-medium">Pages: </span>
        {date.pages.editorSurfaces.length === 0 && date.pages.nonEditingPageCount === 0 ? (
          <span className="text-ink-soft">none recorded</span>
        ) : (
          <>
            {date.pages.editorSurfaces.map((p) => (
              <span
                key={p.path}
                className="inline-block bg-primary-50 text-primary-700 rounded px-2 py-0.5 mr-1 mb-1 text-xs ring-1 ring-primary-400"
              >
                {p.label}
              </span>
            ))}
            {date.pages.nonEditingPageCount > 0 && (
              <span className="inline-block bg-cream text-ink-soft rounded px-2 py-0.5 mr-1 mb-1 text-xs">
                {date.pages.nonEditingPageCount} non-editing page
                {date.pages.nonEditingPageCount === 1 ? '' : 's'}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Feature 015 — per-editor activity detail (FR-018). Fetches the editor's
// retained-window activity, grouped by date, and renders login/logout/duration
// per session plus the pages-visited summary.
export function EditorActivityDetail({ userId }: EditorActivityDetailProps) {
  const [dates, setDates] = useState<EditorActivityDate[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    editorActivityApi
      .getActivity(userId)
      .then((d) => {
        if (!cancelled) setDates(d.dates);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (error) {
    return <p className="text-sm text-red-600">Could not load activity.</p>;
  }

  if (dates === null) {
    return <p className="text-sm text-ink-soft">Loading…</p>;
  }

  if (dates.length === 0) {
    return <p className="text-sm text-ink-soft">No recent activity.</p>;
  }

  return (
    <div>
      {dates.map((d) => (
        <DateBlock key={d.date} date={d} />
      ))}
    </div>
  );
}
